import { Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RadioGroupComponent } from '@group24ag/flowkit';
import { NGXLogger } from 'ngx-logger';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-ui-radio-buttons',
  imports: [RadioGroupComponent, ReactiveFormsModule],
  template: `
    <h1 class="heading-title">Radio Buttons</h1>
    <div class="flex flex-col gap-y-6">
      <ng-template #stringTpl let-option="option">{{ option }}</ng-template>
      <ng-template #objectTpl let-option="option">
        <div class="flex flex-col">
          <span class="font-medium">{{ option.label }}</span>
          <span class="text-dune-400 text-xs">{{ option.description }}</span>
        </div>
      </ng-template>

      <div>
        <h2 class="heading-title--small">Vertical (default)</h2>
        <flk-radio-group
          [formControl]="control1"
          [options]="stringOptions"
          [optionTemplate]="stringTpl" />
      </div>

      <div>
        <h2 class="heading-title--small">Horizontal</h2>
        <flk-radio-group
          class="max-w-full"
          orientation="horizontal"
          [formControl]="control2"
          [options]="stringOptions"
          [optionTemplate]="stringTpl" />
      </div>

      <div>
        <h2 class="heading-title--small">Custom template (objects)</h2>
        <flk-radio-group
          [formControl]="control3"
          [options]="objectOptions"
          [optionTemplate]="objectTpl"
          [compareWith]="compareWith" />
      </div>

      <div>
        <h2 class="heading-title--small">Disabled</h2>
        <flk-radio-group
          [formControl]="disabledControl"
          [options]="stringOptions"
          [optionTemplate]="stringTpl" />
      </div>
    </div>
  `,
})
export class UIRadioButtonsComponent {
  /**  INJECTIONS **/
  /****/
  private readonly logger = inject(NGXLogger);

  /** PROPERTIES **/
  /****/
  protected readonly stringOptions = [
    'Apple',
    'Banana',
    'Cherry',
    'Watermelon',
  ];

  /****/
  protected readonly objectOptions = [
    { id: 1, label: 'Light', description: 'Best for daytime use' },
    { id: 2, label: 'Dark', description: 'Easy on the eyes at night' },
    { id: 3, label: 'System', description: 'Follows your OS setting' },
  ];

  /****/
  protected readonly control1 = new FormControl<string>('Banana');

  /****/
  protected readonly control2 = new FormControl<string | null>(null);

  /****/
  protected readonly control3 = new FormControl({
    id: 2,
    label: 'Dark',
    description: 'Easy on the eyes at night',
  });

  /****/
  protected readonly disabledControl = new FormControl({
    value: 'Apple',
    disabled: true,
  });

  /****/
  protected readonly compareWith = (o1: { id: number }, o2: { id: number }) =>
    o1?.id === o2?.id;

  constructor() {
    this.control1.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => this.logger.log(value));
    this.control2.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => this.logger.log(value));
    this.control3.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => this.logger.log(value));
  }
}
